import React, { useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useForm } from 'react-hook-form';

import Button from '../Button';
import Modal from '../Modal';
import Link from '../Link';
import FormErrors from '../FormErrors';
import { AuthContext } from '../../context/auth-context';

import {
	EMAIL_PATTERN_ERR_MSG,
	PW_REQ_ERR_MSG,
	EMAIL_REGEX,
	EMAIL_REQ_ERR_MSG,
	ERRORS_BY_CODE,
} from '../../data/constants';

import { USE_TEST_DATA, MY_EMAIL, MY_PASSWORD } from '../../utils/config';

const ns = `login-modal`;

//	---------------------
//	Development Test Data
//	---------------------
const testData = {
	defaultValues: undefined,
};

if (USE_TEST_DATA) {
	testData.defaultValues = {
		email: MY_EMAIL,
		password: MY_PASSWORD,
	};
}

const LoginModal = ({
	onRequestClose,
	isLoginModalOpen,
}) => {
	const rootClassnames = classNames({
		[`${ ns }`]: true,
		[`page-account`]: true,
	});

	const { actions: authActions } = useContext(AuthContext);
	const [loginErrors, setLoginErrors] = useState([]);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm({
		defaultValues: {
			email: String(),
			password: String(),
			...testData.defaultValues,
		},
	});

	const close = () => {
		if (onRequestClose && typeof onRequestClose === 'function') {
			onRequestClose();
		}
	};

	const handleLogin = async data => {
		setLoginErrors([]);
		setIsSubmitting(true);

		try {
			await authActions.signIn(data);
			onRequestClose();
		} catch (error) {
			const loginError = {
				...error,
				...(ERRORS_BY_CODE[error.code] || ERRORS_BY_CODE.default),
			};

			setLoginErrors([loginError]);
			console.error(error.json || error);
		}

		setIsSubmitting(false);
	};

	useEffect(() => {
		if (!isLoginModalOpen) {
			setTimeout(() => {
				reset();
			}, [1000]);
		}
	}, [isLoginModalOpen, reset]);

	const emailInputProps = {
		...register('email', {
			required: {
				value: true,
				message: EMAIL_REQ_ERR_MSG,
			},
			pattern: {
				value: EMAIL_REGEX,
				message: EMAIL_PATTERN_ERR_MSG,
			},
		}),
		placeholder: 'Email',
		type: 'text',
	};

	const passwordInputProps = {
		...register('password', {
			required: {
				value: true,
				message: PW_REQ_ERR_MSG,
			},
		}),
		placeholder: 'Password',
		type: 'password',
	};

	const loginErrorsMarkup = !!loginErrors.length && (
		<div
			className={`${ns}__errors`}
			style={{ marginBottom: '20px', marginTop: '10px' }}
		>
			<FormErrors errors={loginErrors} />
		</div>
	);

	return (
		<Modal
			modifier={rootClassnames}
			isOpen={isLoginModalOpen}
			onRequestClose={close}
		>
			<div className={`${ ns }`}>
				<div className={`${ ns }__left-content`}>
					<h3>Welcome Back!</h3>
					<form
						action={''}
						onSubmit={handleSubmit(handleLogin)}
					>
						<div className={`${ ns }__input-wrapper`}>
							<input {...emailInputProps} />
							{errors.email && (
								<span className={'error'}>{errors.email.message}</span>
							)}
						</div>
						<div className={`${ ns }__input-wrapper`}>
							<input {...passwordInputProps} />
							{errors.password && (
								<span className={'error'}>{errors.password.message}</span>
							)}
						</div>
						{loginErrorsMarkup}
						<Link to={'/account/reset'}>
							<small>Forgot your password?</small>
						</Link>
						<Button type={'submit'} disabled={isSubmitting} isLoading={isSubmitting}>
							Log In
						</Button>
						<Link to={'/account/register'}>
							Create account
						</Link>
					</form>
				</div>
				<div className={`${ ns }__right-content`}>
					<h3>For Patients</h3>
					<p>
						Get Nordic Natural products from one of our practitioner partners.
						Use our locator tool to find the closest practitioner to you.
					</p>
					<Button variant={'yellow'} to={'/patients'}>
						Find a Practitioner
					</Button>
					<small>
						<a href={'https://www.nordic.com/'}>Click here</a>
						{' '}
						to visit our consumer site.
					</small>
				</div>
			</div>
		</Modal>
	);
};

export default LoginModal;
